/**
 * CEO Command Center v11 - Transport Service
 *
 * WebRTC-based peer-to-peer transport using PeerJS.
 * Handles connection establishment, message passing, and peer discovery.
 *
 * @module services/sync/transport
 * @version 11.0.0
 */

import Peer, { DataConnection } from 'peerjs';
import type { SyncMessage, DeviceIdentity, TransportService as ITransportService } from '@/types/sync';

// ============================================
// TYPES
// ============================================

/** Local peer connection state */
interface PeerConnection {
  peerId: string;
  connection: DataConnection;
  name: string;
  sharedSpaces: string[];
  lastSeen: number;
}

type MessageHandler = (peerId: string, message: SyncMessage) => void;
type ConnectionHandler = (peerId: string) => void;
type DisconnectionHandler = (peerId: string) => void;

// ============================================
// CONSTANTS
// ============================================

// Use public PeerJS server for now (can self-host later)
const PEERJS_CONFIG = {
  // debug: 3, // Uncomment for debugging
};

// Heartbeat interval (30 seconds)
const HEARTBEAT_INTERVAL = 30000;

// Connection timeout (10 seconds)
const CONNECTION_TIMEOUT = 10000;

// ============================================
// TRANSPORT SERVICE
// ============================================

class TransportServiceImpl implements ITransportService {
  private peer: Peer | null = null;
  private connections: Map<string, PeerConnection> = new Map();
  private rooms: Set<string> = new Set();
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private disconnectionHandlers: Set<DisconnectionHandler> = new Set();
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private _isConnected = false;
  private _peerId: string | null = null;
  private identity: DeviceIdentity | null = null;

  /**
   * Initialize the transport with device identity
   */
  async init(identity: DeviceIdentity): Promise<void> {
    if (this.peer) {
      console.warn('[Transport] Already initialized');
      return;
    }

    this.identity = identity;
    // Use public key as peer ID (truncated for PeerJS compatibility)
    const peerId = `ceocc-${identity.publicKey.substring(0, 20)}`;

    return new Promise((resolve, reject) => {
      console.log('[Transport] Initializing with peer ID:', peerId);

      this.peer = new Peer(peerId, PEERJS_CONFIG);

      this.peer.on('open', (id) => {
        console.log('[Transport] Connected to signaling server, ID:', id);
        this._peerId = id;
        this._isConnected = true;
        this.startHeartbeat();
        resolve();
      });

      this.peer.on('connection', (conn) => {
        console.log('[Transport] Incoming connection from:', conn.peer);
        this.handleIncomingConnection(conn);
      });

      this.peer.on('error', (err) => {
        console.error('[Transport] Peer error:', err);
        if (!this._isConnected) {
          reject(err);
        }
      });

      this.peer.on('disconnected', () => {
        console.log('[Transport] Disconnected from signaling server');
        this._isConnected = false;
        // Try to reconnect
        setTimeout(() => {
          if (this.peer && !this.peer.destroyed) {
            this.peer.reconnect();
          }
        }, 5000);
      });

      this.peer.on('close', () => {
        console.log('[Transport] Peer connection closed');
        this._isConnected = false;
        this.stopHeartbeat();
      });

      // Timeout if connection takes too long
      setTimeout(() => {
        if (!this._isConnected) {
          reject(new Error('Connection timeout'));
        }
      }, CONNECTION_TIMEOUT);
    });
  }

  /**
   * Disconnect from all peers and close transport
   */
  disconnectAll(): void {
    console.log('[Transport] Disconnecting all...');
    this.stopHeartbeat();

    // Close all peer connections
    for (const [peerId, peerConn] of this.connections) {
      peerConn.connection.close();
      this.notifyDisconnection(peerId);
    }
    this.connections.clear();
    this.rooms.clear();

    // Close peer
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    this._isConnected = false;
    this._peerId = null;
    this.identity = null;
  }

  /**
   * Connect to a remote peer
   */
  async connect(remotePeerId: string): Promise<void> {
    if (!this.peer || !this._isConnected) {
      throw new Error('Transport not initialized');
    }

    if (this.connections.has(remotePeerId)) {
      console.log('[Transport] Already connected to:', remotePeerId);
      return;
    }

    console.log('[Transport] Connecting to peer:', remotePeerId);

    return new Promise((resolve, reject) => {
      const conn = this.peer!.connect(remotePeerId, {
        reliable: true,
      });

      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, CONNECTION_TIMEOUT);

      conn.on('open', () => {
        clearTimeout(timeout);
        console.log('[Transport] Connected to peer:', remotePeerId);

        // Send hello message
        this.sendToPeer(conn, {
          type: 'hello',
          publicKey: this.identity?.publicKey || '',
          name: this.identity?.name || 'Unknown',
          spaces: Array.from(this.rooms),
          version: '11.0.0',
        });

        // Store connection (info will be updated when we receive hello)
        this.connections.set(remotePeerId, {
          peerId: remotePeerId,
          connection: conn,
          name: 'Unknown',
          sharedSpaces: [],
          lastSeen: Date.now(),
        });

        this.setupConnectionHandlers(conn, remotePeerId);
        resolve();
      });

      conn.on('error', (err) => {
        clearTimeout(timeout);
        console.error('[Transport] Connection error:', err);
        reject(err);
      });
    });
  }

  /**
   * Disconnect from a remote peer
   */
  disconnect(remotePeerId: string): void {
    const peerConn = this.connections.get(remotePeerId);
    if (peerConn) {
      peerConn.connection.close();
      this.connections.delete(remotePeerId);
      this.notifyDisconnection(remotePeerId);
    }
  }

  /**
   * Send a message to a specific peer
   */
  async send(remotePeerId: string, message: SyncMessage): Promise<void> {
    const peerConn = this.connections.get(remotePeerId);
    if (!peerConn) {
      throw new Error(`Not connected to peer: ${remotePeerId}`);
    }

    this.sendToPeer(peerConn.connection, message);
  }

  /**
   * Broadcast a message to peers in a specific space
   */
  async broadcast(spaceId: string, message: SyncMessage): Promise<void> {
    for (const [, peerConn] of this.connections) {
      // Only send to peers who share this space
      if (peerConn.sharedSpaces.includes(spaceId)) {
        this.sendToPeer(peerConn.connection, message);
      }
    }
  }

  /**
   * Get list of connected peer IDs
   */
  getConnectedPeers(): string[] {
    return Array.from(this.connections.keys());
  }

  /**
   * Check if connected to signaling server
   */
  isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Get local peer ID
   */
  getPeerId(): string | null {
    return this._peerId;
  }

  /**
   * Register a message handler
   */
  onMessage(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  /**
   * Register a connection handler
   */
  onPeerConnected(handler: ConnectionHandler): void {
    this.connectionHandlers.add(handler);
  }

  /**
   * Register a disconnection handler
   */
  onPeerDisconnected(handler: DisconnectionHandler): void {
    this.disconnectionHandlers.add(handler);
  }

  /**
   * Join a room (space) for discovery
   */
  async joinRoom(roomId: string): Promise<void> {
    this.rooms.add(roomId);
    console.log('[Transport] Joined room:', roomId);
    // Broadcast updated spaces to all connected peers
    for (const [peerId] of this.connections) {
      this.sendToPeer(this.connections.get(peerId)!.connection, {
        type: 'hello',
        publicKey: this.identity?.publicKey || '',
        name: this.identity?.name || 'Unknown',
        spaces: Array.from(this.rooms),
        version: '11.0.0',
      });
    }
  }

  /**
   * Leave a room (space)
   */
  leaveRoom(roomId: string): void {
    this.rooms.delete(roomId);
    console.log('[Transport] Left room:', roomId);
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private handleIncomingConnection(conn: DataConnection): void {
    const remotePeerId = conn.peer;

    conn.on('open', () => {
      console.log('[Transport] Incoming connection opened:', remotePeerId);

      // Send hello message
      this.sendToPeer(conn, {
        type: 'hello',
        publicKey: this.identity?.publicKey || '',
        name: this.identity?.name || 'Unknown',
        spaces: Array.from(this.rooms),
        version: '11.0.0',
      });

      // Store connection
      this.connections.set(remotePeerId, {
        peerId: remotePeerId,
        connection: conn,
        name: 'Unknown',
        sharedSpaces: [],
        lastSeen: Date.now(),
      });

      this.setupConnectionHandlers(conn, remotePeerId);
    });
  }

  private setupConnectionHandlers(conn: DataConnection, remotePeerId: string): void {
    conn.on('data', (data) => {
      this.handleMessage(remotePeerId, data as SyncMessage);
    });

    conn.on('close', () => {
      console.log('[Transport] Connection closed:', remotePeerId);
      this.connections.delete(remotePeerId);
      this.notifyDisconnection(remotePeerId);
    });

    conn.on('error', (err) => {
      console.error('[Transport] Connection error with', remotePeerId, ':', err);
    });
  }

  private handleMessage(peerId: string, message: SyncMessage): void {
    const peerConn = this.connections.get(peerId);
    if (peerConn) {
      peerConn.lastSeen = Date.now();
    }

    // Handle hello message to update peer info
    if (message.type === 'hello') {
      if (peerConn) {
        peerConn.name = message.name;
        peerConn.sharedSpaces = message.spaces;
        // Notify connection handlers
        for (const handler of this.connectionHandlers) {
          handler(peerId);
        }
      }
      return;
    }

    // Forward all other messages to handlers
    for (const handler of this.messageHandlers) {
      handler(peerId, message);
    }
  }

  private sendToPeer(conn: DataConnection, message: SyncMessage): void {
    if (conn.open) {
      conn.send(message);
    } else {
      console.warn('[Transport] Cannot send, connection not open');
    }
  }

  private notifyDisconnection(peerId: string): void {
    for (const handler of this.disconnectionHandlers) {
      handler(peerId);
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      // Send hello as heartbeat to all connections
      for (const [, peerConn] of this.connections) {
        this.sendToPeer(peerConn.connection, {
          type: 'hello',
          publicKey: this.identity?.publicKey || '',
          name: this.identity?.name || 'Unknown',
          spaces: Array.from(this.rooms),
          version: '11.0.0',
        });
      }
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// Export singleton instance
export const transportService = new TransportServiceImpl();
export { TransportServiceImpl };
